import { useState, useCallback, useMemo } from 'react';
import { isValidEmail } from '../utils';
import type { UserData } from '../steps/prepared-by';
import type { Step, QuoteDetailsFormData } from '../types';
import { QuoteDetailsSteps } from '../types';

interface UseQuoteDetailsStepsParams {
  formData: QuoteDetailsFormData;
  currentPreparedData: UserData;
  initialStep: QuoteDetailsSteps;
  quoteType: string;
}

export const useQuoteDetailsSteps = ({
  formData,
  currentPreparedData,
  initialStep,
  quoteType
}: UseQuoteDetailsStepsParams) => {
  const [activeStep, setActiveStep] = useState(initialStep);
  const [showWarning, setShowWarning] = useState(false);

  const steps: Step[] = useMemo(
    () => [
      {
        id: 'prepared-for',
        label: 'Prepared For',
        icon: '/icons/quotes-prepared-for.svg',
        completed:
          !!formData.preparedFor?.companyName &&
          !!formData.preparedFor?.address &&
          !!formData?.preparedFor?.phoneNumber,
        hasError:
          !formData.preparedFor?.companyName ||
          !formData.preparedFor?.address ||
          !formData?.preparedFor?.phoneNumber
      },
      {
        id: 'prepared-by',
        label: 'Prepared By',
        icon: '/icons/quotes-prepared-by.svg',
        completed:
          !!currentPreparedData?.firstName &&
          !!currentPreparedData?.lastName &&
          !!currentPreparedData?.contactNumber &&
          isValidEmail(currentPreparedData?.email),
        hasError:
          !currentPreparedData?.firstName ||
          !currentPreparedData?.lastName ||
          !isValidEmail(currentPreparedData?.email) ||
          !currentPreparedData?.contactNumber
      },
      {
        id: 'expiration-date',
        label: 'Expiration Date',
        icon: '/icons/quotes-expiration-date.svg',
        completed: !!formData?.expirationDate,
        hasError: false
      },
      {
        id: 'overall-note',
        label: 'Overall Note',
        icon: '/icons/quotes-overall-note.svg',
        completed: true,
        hasError: false
      },
      ...(quoteType === 'quote'
        ? [
            {
              id: 'quote-display',
              label: 'Quote display',
              icon: '/icons/quotes-file-landscape.svg',
              completed: true,
              hasError: false,
              iconWidth: 20,
              iconHeight: 20
            }
          ]
        : [])
    ],
    [
      currentPreparedData,
      formData.expirationDate,
      formData.preparedFor.address,
      formData.preparedFor.companyName,
      formData.preparedFor?.phoneNumber,
      quoteType
    ]
  );

  const handleStepClick = useCallback((index: number) => {
    setActiveStep(index);
  }, []);

  return {
    steps,
    activeStep,
    handleStepClick,
    showWarning,
    setShowWarning
  };
};
